/**
 * Whether the Speech service accepts the configured key, asked directly. Pure and
 * Angular-free so the reading of the answer can be tested directly.
 *
 * A browser reports a refused key, a wrong region, a disabled resource and a
 * dropped connection alike, as a WebSocket closed with code 1006. The token
 * endpoint answers over plain HTTPS with a status that tells them apart.
 */

/**
 * What the service said about the key.
 *
 * - `accepted`: the key and region are good, so a failed connection is the network.
 * - `refused`: the key, region or resource is wrong or disabled (401, 403).
 * - `throttled`: the resource is over its quota for now (429).
 * - `unknown`: no answer, or one that settles nothing.
 */
export type KeyCheck = 'accepted' | 'refused' | 'throttled' | 'unknown';

/** The token endpoint for `region`, which answers whether a key is accepted there. */
export function keyCheckUrl(region: string): string {
  return `https://${encodeURIComponent(region)}.api.cognitive.microsoft.com/sts/v1.0/issueToken`;
}

/** What an HTTP status from the token endpoint says about the key; `0` or none is no answer. */
export function keyCheckOf(status: number | null | undefined): KeyCheck {
  if (status === 401 || status === 403) {
    return 'refused';
  }
  if (status === 429) {
    return 'throttled';
  }
  return status && status >= 200 && status < 300 ? 'accepted' : 'unknown';
}

/**
 * Whether a connection that failed for good calls for asking the service about the
 * key: the device is online, so the network is not the obvious cause. A key can be
 * refused before the connection first opens or revoked while it is open.
 */
export function shouldCheckKey(offline: boolean): boolean {
  return !offline;
}

/**
 * Whether the service's answer makes a failed connection a configuration error,
 * shown as one, rather than a network to record around. Only a refusal does; a
 * throttled resource, or no answer, is recorded and read later.
 */
export function isConfigurationError(check: KeyCheck | null | undefined): boolean {
  return check === 'refused';
}
